<template>
  <component v-bind="rootTagAttributes" :is="rootTag || 'div'" :class="classes?.root">
    <UiLabel
      v-bind="attributes?.label"
      v-if="label || slots['label']"
      :class="classes?.label"
      :for="inputId"
      :variant="errors?.length ? 'error' : 'default'"
    >
      <slot name="label">
        {{ label }}
      </slot>
    </UiLabel>

    <div v-bind="attributes?.wrapper" :data-value="modelValue" :class="classes?.wrapper" ref="wrapperRef">
      <slot
        v-if="slots['prepend-inner']"
        v-bind="attributes?.prepend"
        name="prepend-inner"
        tabindex="1"
        :class="classes?.prepend"
        @click="inputRef?.focus()"
      />

      <slot name="input" :inputId>
        <input
          v-bind="{ ...$attrs, ...attributes?.input }"
          :value="modelValue"
          @input="emitUpdateModel"
          @blur="emitUpdateModel"
          :id="inputId"
          :type="type"
          :name="name"
          :class="classes?.input"
          :disabled="disabled"
          :placeholder="placeholder"
          ui-input
          tabindex="0"
          ref="inputRef"
        />
      </slot>

      <slot
        v-bind="attributes?.append"
        v-if="slots['append']"
        name="append"
        tabindex="1"
        :class="classes?.append"
      />
    </div>

    <UiErrorStack v-bind="attributes?.errors" :hints :errors :classes="classes?.errors">
      <template #errors="errorBinds">
        <slot v-bind="errorBinds" name="errors" />
      </template>
    </UiErrorStack>
  </component>
</template>

<script lang="ts">
import { type VariantProps, cva } from "class-variance-authority"

import { type UiErrorStackClasses } from "@finq/ui/components/ui/ErrorStack.vue"
import { type UiClassesToAttributes } from "@finq/ui/types/common"

export const inputCommonClasses = {
  root: cn("group flex flex-col leading-none"),
}
export interface UiInputClasses {
  root?: ClassValue
  wrapper?: ClassValue
  label?: ClassValue
  input?: ClassValue
  errors?: Partial<UiErrorStackClasses>
  hint?: ClassValue
  details?: ClassValue
  prepend?: ClassValue
  append?: ClassValue
}

export type UiInputProps = {
  variant?: VariantProps<typeof inputVariants>["variant"]
  size?: VariantProps<typeof inputVariants>["size"]
  type?: string
  name?: string
  label?: string | boolean
  hints?: string[]
  errors?: string | string[]
  hideDetails?: boolean
  placeholder?: string
  disabled?: boolean
  classes?: Partial<UiInputClasses>
  attributes?: UiClassesToAttributes<UiInputClasses>
  rootTag?: string
  valueType?: "string" | "number"
  value?: string | number | undefined
  onClick?: (e: MouseEvent) => void
}

export const inputVariants = cva(
  [
    "w-full overflow-hidden flex items-center justify-center h-full rounded-sm border border-input bg-transparent ring-offset-background placeholder:text-black/50 placeholder:font-light",
    "file:border-0 file:bg-transparent file:text-sm file:font-medium",
    "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-offset-0 focus-visible:ring-primary",
    "focus-within:outline-none focus-within:ring-1 focus-within:ring-offset-0 focus-within:ring-primary",
  ],
  {
    variants: {
      variant: {
        default: "",
        error: "border-destructive focus-within:ring-destructive",
      },
      size: {
        default: "h-10 text-base placeholder:text-sm",
        sm: "h-8 text-sm",
        lg: "h-12 text-lg",
        adaptive: "md:h-12 h-9 text-sm placeholder:text-sm md:text-base md:placeholder:text-base",
      },
    },

    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)
</script>

<script setup lang="ts">
const slots = useSlots()
const instance = getCurrentInstance()
const inputRef = ref<null | HTMLInputElement>(null)
const wrapperRef = ref<null | HTMLInputElement>(null)

defineOptions({ inheritAttrs: false })
defineExpose({ wrapperRef, focus: () => inputRef.value?.focus() })

const inputId = `input-${instance?.uid}`

const props = defineProps<UiInputProps>()
const modelValue = defineModel<string | number | undefined>()

const classes = computed((): UiInputClasses => {
  const fixedVariant = props.errors?.length ? "error" : props.variant

  return {
    root: cn(inputCommonClasses.root, props.classes?.root),
    wrapper: cn(
      "relative",
      { "cursor-not-allowed": props.disabled, "mt-1.5": props.label },
      inputVariants({ variant: fixedVariant, size: props.size, class: props.classes?.wrapper as string })
    ),
    label: cn("mt-xxxs font-light", props.classes?.label),
    input: cn(
      "h-full w-full flex-1 rounded-none bg-transparent px-2 outline-none disabled:pointer-events-none disabled:opacity-50 md:px-4",
      props.classes?.input
    ),
    hint: cn("pt-xxxs text-black/50", props.classes?.hint),
    details: cn(inputCommonClasses.root, props.classes?.details),
    append: cn({ "opacity-50": props.disabled }, props.classes?.append),
    prepend: cn({ "opacity-50": props.disabled }, props.classes?.prepend),
    errors: {
      errors: cn(props.classes?.errors?.errors, props.classes?.details),
      ...props.classes?.errors,
    },
  }
})

function emitUpdateModel(event: any) {
  const value = event.target?.value
  modelValue.value =
    props.valueType === "number" ? (!value || isNaN(Number(value)) ? null : Number(value)) : value
}

const rootTagAttributes = computed(() => {
  if (props.rootTag === "button") {
    return {
      onClick: props.onClick,
    }
  }
})
</script>

<style scoped lang="scss">
input[type="date"] {
  @apply ltr:text-left rtl:text-right;
}
input[type="date"]::-webkit-inner-spin-button,
input[type="date"]::-webkit-calendar-picker-indicator {
  display: none;
  -webkit-appearance: none;
}
input[type="number"]::-webkit-outer-spin-button,
input[type="number"]::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
input[type="number"] {
  -moz-appearance: textfield;
  text-align: left;
}
</style>
